import React, { useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Upload, X } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from '../../ui/avatar'
import { Button } from '../../ui/button'
import { Card } from '../../ui/card'
import { createUploadWidget } from '../../../utilities/cloudinary'

function AvatarUpload({
  cloudinaryPhotoPublicId,
  onPhotoUpload,
  fullName,
  userId: _userId,
  isUploading: externalIsUploading,
  compact = false
}) {
  const [isUploading, setIsUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const widgetRef = useRef(null)

  function getInitials() {
    if (!fullName) return '?'
    return fullName
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  function getAvatarUrl() {
    if (!cloudinaryPhotoPublicId) return null
    return `https://res.cloudinary.com/${process.env.CLOUDINARY_CLOUD_NAME}/image/upload/c_fill,g_face,h_200,w_200/${cloudinaryPhotoPublicId}.jpg`
  }

  const ensureWidget = useCallback(() => {
    if (widgetRef.current) return
    if (!process.env.CLOUDINARY_CLOUD_NAME || !process.env.CLOUDINARY_UPLOAD_PRESET) return
    if (typeof window === 'undefined' || !window.cloudinary) return

    try {
      widgetRef.current = createUploadWidget((error, result) => {
        if (error) {
          setIsUploading(false)
          return
        }

        if (result && result.event === 'upload-added') {
          setIsUploading(true)
        }

        if (result && result.event === 'success') {
          onPhotoUpload(result.info.public_id)
          setIsUploading(false)
        }

        if (result && result.event === 'close') {
          setIsUploading(false)
        }
      })
    } catch (e) {
      widgetRef.current = null
    }
  }, [onPhotoUpload])

  useEffect(() => {
    ensureWidget()
  }, [ensureWidget])

  function handleUploadClick() {
    ensureWidget()
    if (widgetRef.current && widgetRef.current.open) {
      widgetRef.current.open()
    }
  }

  function handleRemovePhoto() {
    onPhotoUpload(null)
  }

  function handleDragEnter(e) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  function handleDragLeave(e) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  function handleDragOver(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  function handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    handleUploadClick()
  }

  const uploading = isUploading || externalIsUploading

  if (compact) {
    return (
      <div className="flex flex-col items-center space-y-4 py-6">
        <div
          className={`relative transition-all ${isDragging ? 'scale-105' : ''}`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Avatar className="h-40 w-40 border-4 border-white shadow-xl ring-2 ring-gray-100">
            {getAvatarUrl() ? (
              <AvatarImage src={getAvatarUrl()} alt={fullName || 'User avatar'} />
            ) : (
              <AvatarFallback className="bg-gradient-to-br from-purple-400 to-purple-600 text-white text-4xl font-semibold">
                {getInitials()}
              </AvatarFallback>
            )}
          </Avatar>
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/90 rounded-full">
              <div className="h-10 w-10 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        <div className="text-center px-4">
          <p className="text-sm font-medium text-gray-900">{fullName}</p>
          <p className="text-xs text-gray-500 mt-1">Profile Photo</p>
        </div>

        <div className="w-full space-y-2 px-4">
          <Button type="button" variant="outline" onClick={handleUploadClick} disabled={uploading} className="w-full">
            <Upload className="h-4 w-4 mr-2" />
            {cloudinaryPhotoPublicId ? 'Change Photo' : 'Upload Photo'}
          </Button>
          {cloudinaryPhotoPublicId && !uploading && (
            <Button
              type="button"
              variant="outline"
              onClick={handleRemovePhoto}
              className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <X className="h-4 w-4 mr-2" />
              Remove Photo
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Card
        className={`relative border-2 border-dashed transition-all ${
          isDragging ? 'border-purple-500 bg-purple-50' : 'border-gray-300'
        } ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-purple-400'}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={!uploading ? handleUploadClick : undefined}
      >
        <div className="flex flex-col items-center gap-4 p-6">
          <Avatar className="h-32 w-32 border-4 border-white shadow-lg">
            {getAvatarUrl() ? (
              <AvatarImage src={getAvatarUrl()} alt={fullName || 'User avatar'} />
            ) : (
              <AvatarFallback className="bg-gradient-to-br from-purple-400 to-purple-600 text-white text-3xl font-semibold">
                {getInitials()}
              </AvatarFallback>
            )}
          </Avatar>

          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Upload className="h-5 w-5 text-gray-400" />
              <p className="text-sm font-medium text-gray-700">
                {uploading ? 'Uploading...' : cloudinaryPhotoPublicId ? 'Change photo' : 'Upload photo'}
              </p>
            </div>
            <p className="text-xs text-gray-500">Click or drag and drop</p>
            <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 10MB</p>
          </div>

          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
              <div className="flex flex-col items-center gap-2">
                <div className="h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm font-medium text-gray-700">Uploading...</p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {cloudinaryPhotoPublicId && !uploading && (
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={handleUploadClick} className="flex-1">
            <Upload className="h-4 w-4 mr-2" />
            Change Photo
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleRemovePhoto}
            className="flex-1 text-red-600 hover:text-red-700"
          >
            <X className="h-4 w-4 mr-2" />
            Remove
          </Button>
        </div>
      )}
    </div>
  )
}

AvatarUpload.propTypes = {
  cloudinaryPhotoPublicId: PropTypes.string,
  onPhotoUpload: PropTypes.func.isRequired,
  fullName: PropTypes.string,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isUploading: PropTypes.bool,
  compact: PropTypes.bool
}

export default AvatarUpload
